/**
 * ParsedQuestionsPreview Component
 * 
 * Displays AI-parsed questions in editable form for user review and modification
 */

import React, { useState, useEffect } from 'react';
import { Edit3, Save, X, AlertCircle, CheckCircle, ArrowLeft, Image as ImageIcon, ZoomIn, Trash2, ToggleLeft, ToggleRight, Upload, Plus } from 'lucide-react';
import Button from '../Button';
import QuestionImageUpload from './QuestionImageUpload';
import { ParsedQuestion } from './types';

const API_URL = import.meta.env.VITE_API_URL || '';

interface ParsedQuestionsPreviewProps {
  parsedQuestions: ParsedQuestion[];
  onQuestionsUpdated: (questions: ParsedQuestion[]) => void;
  onUseQuestions: (questions: ParsedQuestion[]) => void;
  onGoBack: () => void;
  onStartSimulation?: (questions: ParsedQuestion[]) => Promise<void>;
  isLoading?: boolean;
  className?: string;
}

const QUESTION_TYPES = [
  { value: 'single_choice', label: 'Single Choice (Radio)' },
  { value: 'multiple_choice', label: 'Multiple Choice (Checkboxes)' },
  { value: 'yes_no', label: 'Yes/No' },
  { value: 'rating_scale', label: 'Rating Scale' },
  { value: 'likert_scale', label: 'Likert Scale' },
  { value: 'ranking', label: 'Ranking' },
  { value: 'short_text', label: 'Short Text' },
  { value: 'long_text', label: 'Long Text' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'nps', label: 'Net Promoter Score' },
  { value: 'numeric_input', label: 'Numeric Input' },
  { value: 'max_diff', label: 'MaxDiff (Best-Worst Scaling)' },
  { value: 'conjoint_analysis', label: 'Conjoint Analysis' }
];

// Image Modal Component
interface ImageModalProps {
  imageUrl: string;
  description?: string;
  onClose: () => void;
}

const ImageModal: React.FC<ImageModalProps> = ({ imageUrl, description, onClose }) => {
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="relative max-w-5xl max-h-[95vh] bg-white rounded-lg overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute top-3 right-3 bg-white rounded-full p-2 hover:bg-gray-100 transition-colors z-10 shadow-lg"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="overflow-auto max-h-[95vh]">
          <img
            src={imageUrl}
            alt="Full size"
            className="w-full object-contain"
          />
          {description && (
            <div className="p-6 bg-gray-50 border-t">
              <div className="flex items-start mb-2">
                <AlertCircle className="w-4 h-4 text-blue-500 mr-2 mt-0.5" />
                <h4 className="text-sm font-semibold text-gray-800">AI Description</h4>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {description}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ParsedQuestionsPreview: React.FC<ParsedQuestionsPreviewProps> = ({
  parsedQuestions,
  onQuestionsUpdated,
  onUseQuestions,
  onGoBack,
  onStartSimulation,
  isLoading = false,
  className = ''
}) => {
  const [questions, setQuestions] = useState<ParsedQuestion[]>(parsedQuestions);
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewingImage, setViewingImage] = useState<{url: string, description?: string} | null>(null);
  // Store original state before editing to enable proper Cancel functionality
  const [originalQuestionState, setOriginalQuestionState] = useState<ParsedQuestion | null>(null);

  useEffect(() => {
    setQuestions(parsedQuestions);
    setHasChanges(false);
  }, [parsedQuestions]);

  const updateQuestion = (questionId: string, updates: Partial<ParsedQuestion>) => {
    const updatedQuestions = questions.map(q => 
      q.id === questionId ? { ...q, ...updates } : q
    );
    setQuestions(updatedQuestions);
    setHasChanges(true);
    onQuestionsUpdated(updatedQuestions);
  };

  const addChoice = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question) {
      const choices = question.choices || [];
      updateQuestion(questionId, {
        choices: [...choices, `Option ${choices.length + 1}`]
      });
    }
  };

  const removeChoice = (questionId: string, choiceIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.choices) {
      const choices = question.choices.filter((_, index) => index !== choiceIndex);
      updateQuestion(questionId, { choices });
    }
  };

  const updateChoice = (questionId: string, choiceIndex: number, value: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.choices) {
      const choices = [...question.choices];
      choices[choiceIndex] = value;
      updateQuestion(questionId, { choices });
    }
  };

  const removeQuestion = (questionId: string) => {
    const updatedQuestions = questions.filter(q => q.id !== questionId);
    setQuestions(updatedQuestions);
    setHasChanges(true);
    onQuestionsUpdated(updatedQuestions);
    // Close edit mode if deleting the currently edited question
    if (editingQuestionId === questionId) {
      setEditingQuestionId(null);
    }
  };

  const handleImageUploadInEdit = (questionId: string, imageData: {path: string, url: string, file: File}) => {
    // Simply add image path to question - descriptions will be generated in batch on backend
    setQuestions(prevQuestions => {
      const updated = prevQuestions.map(q => {
        if (q.id === questionId) {
          return {
            ...q,
            image_paths: [...(q.image_paths || []), imageData.url]
          };
        }
        return q;
      });
      onQuestionsUpdated(updated);
      return updated;
    });
    setHasChanges(true);
  };

  const handleImageRemoveInEdit = (questionId: string, imageIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.image_paths) {
      const updatedPaths = question.image_paths.filter((_, idx) => idx !== imageIndex);
      const updatedDescriptions = question.image_descriptions 
        ? question.image_descriptions.filter((_, idx) => idx !== imageIndex)
        : [];
      
      // If option-level attachment, also update choices and option_images
      let updates: Partial<ParsedQuestion> = {
        image_paths: updatedPaths,
        image_descriptions: updatedDescriptions
      };
      
      if (question.image_attachment_level === 'option' && question.choices && question.option_images) {
        // Remove corresponding choice and option_image entry
        const updatedChoices = [...question.choices];
        const removedChoice = updatedChoices[imageIndex];
        updatedChoices.splice(imageIndex, 1);
        
        const updatedOptionImages = { ...question.option_images };
        delete updatedOptionImages[removedChoice];
        
        updates.choices = updatedChoices;
        updates.option_images = updatedOptionImages;
      }
      
      // Update paths and descriptions (remove both at same index)
      updateQuestion(questionId, updates);
    }
  };

  const handleAttachmentLevelToggle = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (!question) return;

    const currentLevel = question.image_attachment_level || 'question';
    const newLevel = currentLevel === 'question' ? 'option' : 'question';

    if (newLevel === 'option') {
      // Convert to option-level: create choices from images
      const imagePaths = question.image_paths || [];
      const newChoices = imagePaths.map((_, idx) => `Image ${idx + 1}`);
      const newOptionImages: Record<string, string> = {};
      newChoices.forEach((choice, idx) => {
        if (imagePaths[idx]) {
          newOptionImages[choice] = imagePaths[idx];
        }
      });
      
      updateQuestion(questionId, {
        image_attachment_level: 'option',
        choices: newChoices,
        option_images: newOptionImages,
        type: question.type === 'single_choice' || question.type === 'multiple_choice' ? question.type : 'single_choice'
      });
    } else {
      // Convert to question-level: keep images, remove option_images mapping
      updateQuestion(questionId, {
        image_attachment_level: 'question',
        option_images: undefined
      });
    }
  };

  const handleOptionImageUpload = async (questionId: string, optionIndex: number, imageData: string) => {
    const question = questions.find(q => q.id === questionId);
    if (!question) return;
    
    try {
      // Convert base64 to File
      const response = await fetch(imageData);
      const blob = await response.blob();
      const file = new File([blob], `option_${optionIndex}_${Date.now()}.jpg`, { type: 'image/jpeg' });
      
      // Upload image
      const formData = new FormData();
      formData.append('file', file);
      
      const uploadResponse = await fetch(`${API_URL}/api/question-images/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      const data = await uploadResponse.json();
      
      // Update option_images mapping
      const choiceLabel = question.choices?.[optionIndex];
      if (choiceLabel && data.image_url) {
        updateQuestion(questionId, {
          option_images: {
            ...question.option_images,
            [choiceLabel]: data.image_url
          }
        });
      }
    } catch (error) {
      console.error('Failed to upload option image:', error);
    }
  };

  const handleOptionImageRemove = (questionId: string, optionIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (!question || !question.choices) return;
    
    const choiceLabel = question.choices[optionIndex];
    const updatedOptionImages = { ...question.option_images };
    delete updatedOptionImages[choiceLabel];
    
    updateQuestion(questionId, {
      option_images: updatedOptionImages
    });
  };

  const getQuestionTypeLabel = (type: string) => {
    const questionType = QUESTION_TYPES.find(qt => qt.value === type);
    return questionType ? questionType.label : type;
  };

  const hasChoices = (type: string) => {
    return ['single_choice', 'multiple_choice', 'dropdown', 'ranking'].includes(type);
  };

  const hasScale = (type: string) => {
    return ['rating_scale', 'likert_scale', 'nps'].includes(type);
  };

  const hasMaxDiff = (type: string) => {
    return type === 'max_diff';
  };

  const hasConjoint = (type: string) => {
    return type === 'conjoint_analysis';
  };

  const addMaxDiffItem = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question) {
      const items = question.maxdiff_items || [];
      updateQuestion(questionId, {
        maxdiff_items: [...items, `Item ${items.length + 1}`]
      });
    }
  };

  const removeMaxDiffItem = (questionId: string, itemIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.maxdiff_items) {
      const items = question.maxdiff_items.filter((_, index) => index !== itemIndex);
      updateQuestion(questionId, { maxdiff_items: items });
    }
  };

  const updateMaxDiffItem = (questionId: string, itemIndex: number, value: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.maxdiff_items) {
      const items = [...question.maxdiff_items];
      items[itemIndex] = value;
      updateQuestion(questionId, { maxdiff_items: items });
    }
  };

  const addConjointAttribute = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question) {
      const attributes = question.conjoint_attributes || [];
      updateQuestion(questionId, {
        conjoint_attributes: [...attributes, { name: `Attribute ${attributes.length + 1}`, levels: ['Level 1', 'Level 2'] }]
      });
    }
  };

  const removeConjointAttribute = (questionId: string, attrIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.conjoint_attributes) {
      const attributes = question.conjoint_attributes.filter((_, index) => index !== attrIndex);
      updateQuestion(questionId, { conjoint_attributes: attributes });
    }
  };

  const updateConjointAttributeName = (questionId: string, attrIndex: number, name: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.conjoint_attributes) {
      const attributes = [...question.conjoint_attributes];
      attributes[attrIndex] = { ...attributes[attrIndex], name };
      updateQuestion(questionId, { conjoint_attributes: attributes });
    }
  };

  const addConjointLevel = (questionId: string, attrIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.conjoint_attributes) {
      const attributes = [...question.conjoint_attributes];
      const levels = attributes[attrIndex].levels || [];
      attributes[attrIndex] = {
        ...attributes[attrIndex],
        levels: [...levels, `Level ${levels.length + 1}`]
      };
      updateQuestion(questionId, { conjoint_attributes: attributes });
    }
  };

  const removeConjointLevel = (questionId: string, attrIndex: number, levelIndex: number) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.conjoint_attributes) {
      const attributes = [...question.conjoint_attributes];
      const levels = attributes[attrIndex].levels.filter((_, index) => index !== levelIndex);
      attributes[attrIndex] = { ...attributes[attrIndex], levels };
      updateQuestion(questionId, { conjoint_attributes: attributes });
    }
  };

  const updateConjointLevel = (questionId: string, attrIndex: number, levelIndex: number, value: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question && question.conjoint_attributes) {
      const attributes = [...question.conjoint_attributes];
      const levels = [...attributes[attrIndex].levels];
      levels[levelIndex] = value;
      attributes[attrIndex] = { ...attributes[attrIndex], levels };
      updateQuestion(questionId, { conjoint_attributes: attributes });
    }
  };

  const handleUseQuestions = async () => {
    if (isSubmitting) return;
    
    // If we have a direct simulation function, run it
    if (onStartSimulation) {
      setIsSubmitting(true);
      try {
        await onStartSimulation(questions);
      } catch (error) {
        console.error('Failed to start simulation:', error);
        setIsSubmitting(false);
      }
    } else {
      // Fallback to the original behavior
      onUseQuestions(questions);
    }
  };

  if (isLoading) {
    return (
      <div className={`bg-white rounded-xl shadow-sm p-6 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-3"></div>
          <span className="text-gray-600">Processing parsed questions...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm p-6 ${className}`}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onGoBack}
            icon={<ArrowLeft className="w-4 h-4 mr-1" />}
          >
            Back to Questions
          </Button>
          {hasChanges && (
            <div className="flex items-center text-amber-600 text-sm">
              <AlertCircle className="w-4 h-4 mr-1" />
              You have unsaved changes
            </div>
          )}
        </div>
        
        <div className="flex items-center mb-2">
          <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
          <h2 className="text-xl font-semibold text-gray-900">
            Review Questions
          </h2>
        </div>
        <p className="text-gray-600">
          Review and edit the AI-parsed questions below. Click on any question to modify it.
        </p>
        
        {/* Show image summary if any questions have images */}
        {(() => {
          const questionsWithImages = questions.filter(q => q.image_paths && q.image_paths.length > 0);
          const totalImages = questions.reduce((sum, q) => sum + (q.image_paths?.length || 0), 0);
          
          if (totalImages > 0) {
            return (
              <div className="mt-3 flex items-center text-sm text-blue-700 bg-blue-50 px-3 py-2 rounded-md">
                <ImageIcon className="w-4 h-4 mr-2" />
                <span>
                  {totalImages} {totalImages === 1 ? 'image' : 'images'} across {questionsWithImages.length} {questionsWithImages.length === 1 ? 'question' : 'questions'}
                </span>
              </div>
            );
          }
          return null;
        })()}
      </div>

      {/* Questions List */}
      <div className="space-y-6 mb-6">
        {questions.map((question, index) => (
          <div
            key={question.id}
            className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
          >
            {/* Question Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <span className="text-sm font-medium text-gray-500 mr-3">
                    Q{index + 1}
                  </span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {getQuestionTypeLabel(question.type)}
                  </span>
                </div>
                
                {editingQuestionId === question.id ? (
                  <div className="space-y-3">
                    <textarea
                      value={question.text}
                      onChange={(e) => updateQuestion(question.id, { text: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md resize-none"
                      rows={2}
                      placeholder="Enter question text"
                    />
                    
                    <select
                      value={question.type}
                      onChange={(e) => updateQuestion(question.id, { type: e.target.value })}
                      className="px-3 py-2 border border-gray-300 rounded-md"
                    >
                      {QUESTION_TYPES.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>

                    <div>
                      {/* Only show QuestionImageUpload if attachment is at question level */}
                      {question.image_attachment_level !== 'option' && (
                        <>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Question Images
                          </label>
                          <QuestionImageUpload
                            questionIndex={index}
                            onImageUpload={(_, imageData) => handleImageUploadInEdit(question.id, imageData)}
                            onImageRemove={(_, imageIndex) => handleImageRemoveInEdit(question.id, imageIndex)}
                            existingImageUrls={question.image_paths || []}
                            maxImages={5}
                          />
                        </>
                      )}
                      
                      {/* Image attachment level toggle - show for both levels */}
                      {question.image_paths && question.image_paths.length > 0 && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-1">Image Attachment</p>
                              <p className="text-xs text-gray-500">
                                {question.image_attachment_level === 'option' 
                                  ? 'Images are the selectable options'
                                  : 'Images provide context for the question'}
                              </p>
                            </div>
                            <button
                              onClick={() => handleAttachmentLevelToggle(question.id)}
                              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded-md transition-colors bg-blue-100 text-blue-700 hover:bg-blue-200"
                            >
                              {question.image_attachment_level === 'option' ? (
                                <>
                                  <ToggleRight className="w-4 h-4" />
                                  <span>As Options</span>
                                </>
                              ) : (
                                <>
                                  <ToggleLeft className="w-4 h-4" />
                                  <span>As Context</span>
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-900 font-medium">{question.text}</p>
                )}
              </div>
              
              <div className="flex items-center space-x-2 ml-4">
                {editingQuestionId === question.id ? (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingQuestionId(null)}
                      icon={<Save className="w-4 h-4" />}
                    >
                      Save
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        // Restore from original state to revert ALL changes
                        if (originalQuestionState) {
                          setQuestions(prevQuestions => 
                            prevQuestions.map(q => 
                              q.id === originalQuestionState.id ? originalQuestionState : q
                            )
                          );
                          setOriginalQuestionState(null);
                        }
                        setEditingQuestionId(null);
                        setHasChanges(false);
                      }}
                      icon={<X className="w-4 h-4" />}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        // Save original state before editing
                        setOriginalQuestionState(JSON.parse(JSON.stringify(question)));
                        setEditingQuestionId(question.id);
                      }}
                      icon={<Edit3 className="w-4 h-4" />}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeQuestion(question.id)}
                      icon={<Trash2 className="w-4 h-4" />}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Delete question"
                    >
                      Delete
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Question-Level Images Section - Show in VIEW mode */}
            {editingQuestionId !== question.id && question.image_attachment_level !== 'option' && (
              <div className="mt-3 mb-4">
                {/* Show existing images if any */}
                {question.image_paths && question.image_paths.length > 0 ? (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <ImageIcon className="w-4 h-4 text-gray-500 mr-2" />
                        <span className="text-sm font-medium text-gray-700">
                          {question.image_paths.length} {question.image_paths.length === 1 ? 'Image' : 'Images'} (Context)
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
                      {question.image_paths.map((imagePath, imgIndex) => {
                        const fullImageUrl = imagePath.startsWith('http') ? imagePath : `${API_URL}${imagePath}`;
                        const imageDescription = question.image_descriptions?.[imgIndex];
                        
                        return (
                          <div key={imgIndex} className="space-y-1 relative group">
                            <div 
                              className="relative cursor-pointer"
                              onClick={() => setViewingImage({url: fullImageUrl, description: imageDescription})}
                            >
                              <img
                                src={fullImageUrl}
                                alt={`Question ${index + 1} - Image ${imgIndex + 1}`}
                                className="w-full h-32 object-cover rounded border border-gray-300 hover:border-blue-400 transition-colors"
                              />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded flex items-center justify-center opacity-0 group-hover:opacity-100">
                                <div className="bg-white rounded-full p-2">
                                  <ZoomIn className="w-5 h-5 text-gray-700" />
                                </div>
                              </div>
                            </div>
                            {/* Remove button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleImageRemoveInEdit(question.id, imgIndex);
                              }}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                              title="Remove image"
                            >
                              <X className="w-4 h-4" />
                            </button>
                            {imageDescription && (
                              <p className="text-xs text-gray-600 italic line-clamp-2" title={imageDescription}>
                                {imageDescription}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {/* Add more images */}
                    <QuestionImageUpload
                      questionIndex={index}
                      onImageUpload={(_, imageData) => handleImageUploadInEdit(question.id, imageData)}
                      onImageRemove={(_, imageIndex) => handleImageRemoveInEdit(question.id, imageIndex)}
                      existingImageUrls={[]}
                      maxImages={5 - (question.image_paths?.length || 0)}
                    />
                  </>
                ) : question.image_attachment_level === 'question' ? (
                  /* AI indicated this question should have images but none uploaded yet */
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-center mb-2">
                      <ImageIcon className="w-4 h-4 text-blue-600 mr-2" />
                      <span className="text-sm font-medium text-blue-700">Add Question Images</span>
                      <span className="ml-2 text-xs text-blue-500">(AI detected this question expects images)</span>
                    </div>
                    <QuestionImageUpload
                      questionIndex={index}
                      onImageUpload={(_, imageData) => handleImageUploadInEdit(question.id, imageData)}
                      onImageRemove={(_, imageIndex) => handleImageRemoveInEdit(question.id, imageIndex)}
                      existingImageUrls={[]}
                      maxImages={5}
                    />
                  </div>
                ) : null}
              </div>
            )}

            {/* Question Details */}
            <div className="mt-3 space-y-3">
              {/* Choices for choice-based questions */}
              {hasChoices(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Answer Choices
                    {question.image_attachment_level === 'option' && (
                      <span className="ml-2 text-xs text-blue-600 font-normal">(with images)</span>
                    )}
                  </label>
                  {question.image_attachment_level === 'option' ? (
                    // OPTION-LEVEL IMAGES: Show upload in both edit and view mode
                    <div className="flex flex-wrap gap-3 mt-2">
                      {(question.choices || []).map((choice, i) => {
                        const imageUrl = question.option_images?.[choice];
                        const fullImageUrl = imageUrl ? (imageUrl.startsWith('http') ? imageUrl : `${API_URL}${imageUrl}`) : null;
                        
                        return (
                          <div key={i} className="relative group">
                            {/* Number badge */}
                            <div className="absolute top-1 left-1 bg-gray-900 bg-opacity-75 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center z-10">
                              {i + 1}
                            </div>
                            
                            {fullImageUrl ? (
                              <>
                                <img
                                  src={fullImageUrl}
                                  alt={`Option ${i + 1}`}
                                  className="h-32 w-32 object-cover rounded border-2 border-gray-300 cursor-pointer hover:border-blue-400 transition-colors"
                                  onClick={() => setViewingImage({url: fullImageUrl})}
                                />
                                {/* X button on top-right */}
                                <button
                                  onClick={() => handleOptionImageRemove(question.id, i)}
                                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                  title="Remove image"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              // Upload placeholder
                              <>
                                <label className="h-32 w-32 flex flex-col items-center justify-center bg-blue-50 rounded border-2 border-dashed border-blue-300 cursor-pointer hover:border-blue-400 hover:bg-blue-100 transition-colors">
                                  <Upload className="w-6 h-6 text-blue-400 mb-1" />
                                  <span className="text-xs text-blue-500">Upload</span>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => {
                                      if (e.target.files?.[0]) {
                                        const reader = new FileReader();
                                        reader.onload = () => {
                                          handleOptionImageUpload(question.id, i, reader.result as string);
                                        };
                                        reader.readAsDataURL(e.target.files[0]);
                                      }
                                    }}
                                  />
                                </label>
                                {/* X button for empty upload box - only show in edit mode */}
                                {editingQuestionId === question.id && (
                                  <button
                                    onClick={() => removeChoice(question.id, i)}
                                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                    title="Remove option"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                      
                      {/* Add Option button - always visible for option-level */}
                      <div className="h-32 w-32 flex items-center justify-center">
                        <button
                          onClick={() => addChoice(question.id)}
                          className="w-full h-full border-2 border-dashed border-gray-300 rounded hover:border-blue-400 flex flex-col items-center justify-center text-gray-500 transition-colors"
                        >
                          <Plus className="w-6 h-6 mb-1" />
                          <span className="text-xs">Add Option</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    // REGULAR VERTICAL LIST: For text choices or edit mode of non-option-level images
                    <div className="space-y-2">
                      {(question.choices || []).map((choice, choiceIndex) => (
                        <div key={choiceIndex} className="flex items-center space-x-2">
                          <span className="text-sm text-gray-500 w-6 flex-shrink-0">
                            {choiceIndex + 1}.
                          </span>
                          {editingQuestionId === question.id ? (
                            <>
                              <input
                                type="text"
                                value={choice}
                                onChange={(e) => updateChoice(question.id, choiceIndex, e.target.value)}
                                className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                              />
                              <button
                                onClick={() => removeChoice(question.id, choiceIndex)}
                                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                title="Remove choice"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </>
                          ) : (
                            <span className="text-sm text-gray-900">{choice}</span>
                          )}
                        </div>
                      ))}
                      {editingQuestionId === question.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => addChoice(question.id)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          + Add Choice
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Scale display for scale-based questions (always visible) */}
              {hasScale(question.type) && (
                <div>
                  {editingQuestionId === question.id ? (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Min Value
                        </label>
                        <input
                          type="number"
                          value={question.scale_min || 1}
                          onChange={(e) => updateQuestion(question.id, { scale_min: parseInt(e.target.value) })}
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Max Value
                        </label>
                        <input
                          type="number"
                          value={question.scale_max || 5}
                          onChange={(e) => updateQuestion(question.id, { scale_max: parseInt(e.target.value) })}
                          className="w-full px-3 py-1 border border-gray-300 rounded text-sm"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-4">
                      <span className="text-sm font-medium text-gray-700">Scale Range:</span>
                      <span className="text-sm text-gray-900">
                        {question.scale_min || 1} - {question.scale_max || (question.type === 'nps' ? 10 : 5)}
                      </span>
                      {question.type === 'likert_scale' && (
                        <span className="text-xs text-gray-600 italic">
                          (Strongly Disagree to Strongly Agree)
                        </span>
                      )}
                      {question.type === 'nps' && (
                        <span className="text-xs text-gray-600 italic">
                          (Not at all likely to Extremely likely)
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Additional metadata display */}
              {question.type === 'multiple_choice' && question.max_selections && editingQuestionId !== question.id && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Max Selections:</span>
                  <span className="text-sm text-gray-900">{question.max_selections}</span>
                </div>
              )}

              {question.type === 'ranking' && question.ranking_count && editingQuestionId !== question.id && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Rank Top:</span>
                  <span className="text-sm text-gray-900">{question.ranking_count} choices</span>
                </div>
              )}

              {(question.type === 'short_text' || question.type === 'long_text') && question.text_limit && editingQuestionId !== question.id && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Character Limit:</span>
                  <span className="text-sm text-gray-900">{question.text_limit}</span>
                </div>
              )}

              {/* MaxDiff Items */}
              {hasMaxDiff(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    MaxDiff Items
                    {question.maxdiff_items && question.maxdiff_items.length > 0 && editingQuestionId !== question.id && (
                      <span className="ml-2 text-xs text-blue-600 font-normal">
                        ({question.maxdiff_items.length} items, 4 per set, {Math.max(3, Math.ceil(question.maxdiff_items.length / 4))} sets per persona)
                      </span>
                    )}
                  </label>
                  <div className="space-y-2">
                    {(question.maxdiff_items || []).map((item, itemIndex) => (
                      <div key={itemIndex} className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500 w-6 flex-shrink-0">
                          {itemIndex + 1}.
                        </span>
                        {editingQuestionId === question.id ? (
                          <>
                            <input
                              type="text"
                              value={item}
                              onChange={(e) => updateMaxDiffItem(question.id, itemIndex, e.target.value)}
                              className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                            />
                            <button
                              onClick={() => removeMaxDiffItem(question.id, itemIndex)}
                              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                              title="Remove item"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </>
                        ) : (
                          <span className="text-sm text-gray-900">{item}</span>
                        )}
                      </div>
                    ))}
                    {editingQuestionId === question.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => addMaxDiffItem(question.id)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        + Add Item
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Conjoint Attributes */}
              {hasConjoint(question.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Conjoint Attributes
                  </label>
                  {editingQuestionId === question.id && (
                    <div className="mb-3">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Task Description
                      </label>
                      <textarea
                        value={question.conjoint_description || ''}
                        onChange={(e) => updateQuestion(question.id, { conjoint_description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                        rows={2}
                        placeholder="Describe the choice task (e.g., 'Select your preferred smartphone')"
                      />
                    </div>
                  )}
                  <div className="space-y-4">
                    {(question.conjoint_attributes || []).map((attr, attrIndex) => (
                      <div key={attrIndex} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          {editingQuestionId === question.id ? (
                            <input
                              type="text"
                              value={attr.name}
                              onChange={(e) => updateConjointAttributeName(question.id, attrIndex, e.target.value)}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm font-medium mr-2"
                              placeholder="Attribute name"
                            />
                          ) : (
                            <span className="text-sm font-medium text-gray-800">{attr.name}</span>
                          )}
                          {editingQuestionId === question.id && (
                            <button
                              onClick={() => removeConjointAttribute(question.id, attrIndex)}
                              className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                              title="Remove attribute"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <div className="space-y-1">
                          <span className="text-xs text-gray-600">Levels:</span>
                          <div className="flex flex-wrap gap-2">
                            {attr.levels.map((level, levelIndex) => (
                              <div key={levelIndex} className="flex items-center">
                                {editingQuestionId === question.id ? (
                                  <div className="flex items-center space-x-1">
                                    <input
                                      type="text"
                                      value={level}
                                      onChange={(e) => updateConjointLevel(question.id, attrIndex, levelIndex, e.target.value)}
                                      className="px-2 py-1 border border-gray-300 rounded text-xs w-24"
                                    />
                                    <button
                                      onClick={() => removeConjointLevel(question.id, attrIndex, levelIndex)}
                                      className="p-0.5 text-gray-400 hover:text-red-500 transition-colors"
                                      title="Remove level"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                ) : (
                                  <span className="px-2 py-1 bg-teal-50 text-teal-700 rounded text-xs">
                                    {level}
                                  </span>
                                )}
                              </div>
                            ))}
                            {editingQuestionId === question.id && (
                              <button
                                onClick={() => addConjointLevel(question.id, attrIndex)}
                                className="px-2 py-1 border border-dashed border-gray-300 rounded text-xs text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors"
                              >
                                + Add Level
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {editingQuestionId === question.id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => addConjointAttribute(question.id)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        + Add Attribute
                      </Button>
                    )}
                  </div>
                  {editingQuestionId !== question.id && question.conjoint_description && (
                    <div className="mt-2 text-xs text-gray-600 italic">
                      Task: {question.conjoint_description}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="pt-4 border-t border-gray-200">
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-600">
            {questions.length} question{questions.length !== 1 ? 's' : ''} parsed
          </p>
          
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={onGoBack}
            >
              Re-enter Questions
            </Button>
            <Button
              variant="primary"
              onClick={handleUseQuestions}
              disabled={editingQuestionId !== null || isSubmitting}
            >
              {isSubmitting ? 'Starting...' : 'Start Simulation'}
            </Button>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {viewingImage && (
        <ImageModal
          imageUrl={viewingImage.url}
          description={viewingImage.description}
          onClose={() => setViewingImage(null)}
        />
      )}
    </div>
  );
};

export default ParsedQuestionsPreview;